/**
 * ATTEST-UX-2 D2 — land the CPA on the field, not on the screen.
 *
 * "Correct here" used to call `setStep` and stop. On Step 3 that drops the CPA
 * at the top of a form with eleven inputs and the starred one somewhere below
 * the fold — the pointer named the step and then abandoned them in it.
 *
 * The anchor is *queued* rather than focused inline, because on a cross-step
 * jump the target does not exist yet when the click handler runs. React batches
 * the step change and the queued anchor into one render, so the effect below
 * fires after the new step has mounted and `getElementById` can find it. That
 * is deterministic; a `setTimeout(…, 0)` racing the commit is not.
 *
 * Two deliberate non-failures:
 *
 * - **No element, no jump.** Several preflight rows describe a state rather than
 *   a field (an impairment, an eligibility problem); those pass no anchor and
 *   get the step-level behaviour they had before. A wrong target is worse than
 *   none, and so is throwing.
 * - **`scrollIntoView` is optional-called.** jsdom does not implement it, and a
 *   help affordance is not worth failing a test suite over.
 */
import { useCallback, useEffect, useState } from 'react'

/**
 * @param step  the wizard's current step — re-runs the effect once the new
 *              step has rendered, which is when the anchor becomes findable.
 * @returns     queue a field to land on. `null`/`undefined` clears the queue,
 *              which is how "jump to the step, no particular field" is said.
 */
export function useFieldAnchor(step: string): (fieldId?: string | null) => void {
  const [pending, setPending] = useState<string | null>(null)

  useEffect(() => {
    if (!pending) return
    setPending(null)
    const el = document.getElementById(pending)
    if (!el) return
    el.scrollIntoView?.({ block: 'center' })
    el.focus?.({ preventScroll: true })
  }, [pending, step])

  return useCallback((fieldId?: string | null) => setPending(fieldId ?? null), [])
}
